import { onlineManager } from "@tanstack/react-query"
import { queryClient } from "./queryClient"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister"
import { useEffect } from "react"

// Register all mutation defaults (must happen before hydration)
import "./registerMutations"

// Set up scope cascade retry (auto-retries blocked mutations when prerequisites succeed)
import { setupScopeCascadeRetry } from "./scopeCascadeRetry"
setupScopeCascadeRetry()

import { retryNetworkFailedMutations } from "./retryNetworkFailedMutations"
import { reconstituteMutationErrors } from "./reconstituteMutationErrors"
import { shouldPersistQuery } from "./persistPredicate"

// Fix stale OnlineManager default: TanStack Query initialises #online = true
// and only reacts to online/offline transition events. If the app loads while
// already offline, no "offline" event fires, so TQ incorrectly believes it is
// online. This causes focus-triggered resumePausedMutations() to execute
// mutations that immediately fail. See: https://github.com/TanStack/query/issues/10142
if (!navigator.onLine) {
  onlineManager.setOnline(false)
}

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
  key: "fieldbook.persistData",
})

export const FieldbookPersistQueryClientProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  // Resume paused mutations when online state detected
  useEffect(() => {
    const onOnline = () => {
      console.log("just went online")
      // Retry network-failed mutations BEFORE resuming paused ones.
      // This ensures rebuilt CREATEs enter the cache as pending in their
      // scope before dependent paused mutations resume. TanStack Query's
      // scope serialization then holds dependents until the CREATE completes.
      retryNetworkFailedMutations()
      queryClient.resumePausedMutations()
    }

    window.addEventListener("online", onOnline)
    return () => window.removeEventListener("online", onOnline)
  }, [])

  return (
    <PersistQueryClientProvider
      persistOptions={{
        persister,
        buster: __BUILD_ID__,
        // Don't persist the Supabase session into localStorage. It is
        // already durably stored in IndexedDB by the custom auth storage
        // (packages/common/authStorage.ts) and persisting a duplicate here
        // risks drift: e.g. offline logout nulls the cache but can't clear
        // IDB, so on next startup the two stores disagree. The session
        // query's queryFn rehydrates from IDB at mount, which is fast.
        // ["auth", "orgUser"] is still persisted — it's the fallback for
        // pre-hook sessions and needs to survive offline reloads until
        // the hook has been live long enough to drop the fallback entirely.
        //
        // shouldPersistQuery also re-applies the client default's
        // `meta.persisted !== false` opt-out: query-core's dehydrate uses
        // options.shouldDehydrateQuery when supplied and never consults the
        // client default, so the two would otherwise disagree and the
        // opt-outs on the large online context queries would be ignored.
        dehydrateOptions: {
          shouldDehydrateQuery: shouldPersistQuery,
        },
      }}
      client={queryClient}
      onSuccess={async () => {
        // Reconstitute error objects that lost their prototype chain
        // during JSON serialization. Without this, type guards like
        // isMutationError() and isPrerequisiteFailedError() fail on
        // rehydrated errors, breaking retry logic and UI classification.
        reconstituteMutationErrors()

        // After rehydration from a full app restart (not just refresh),
        // pending mutations lose their isPaused flag. We need to re-pause
        // them so they'll resume when we come back online.
        const pendingNotPaused = queryClient.getMutationCache().findAll({
          status: "pending",
          predicate: ({ state }) => !state.isPaused,
        })

        if (pendingNotPaused.length > 0) {
          for (const mutation of pendingNotPaused) {
            mutation.state.isPaused = true
          }
        }
        // do not resume paused mutations here, navigator.isOnline is unreliable at startup (even with delay)
      }}
    >
      {children}
    </PersistQueryClientProvider>
  )
}
